import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { AuthLayout, Field } from "./AuthLayout";
import { PremiumInput } from "../../components/brand/PremiumInput";
import { useAuth } from "../../data/authStore";
import { apiError } from "../../lib/api";

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setToken(params.get("token") || ""); }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Reset token is missing — request a new link."); return; }
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== pw2)   { setError("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await resetPassword({ token, new_password: pw });
      toast.success("Password updated", { description: "You're now signed in." });
      nav("/today", { replace: true });
    } catch (e) {
      const msg = apiError(e);
      setError(msg);
      toast.error("Reset failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      testid="reset-password-page"
      eyebrow="Account recovery"
      title="Set a new password"
      subtitle="Choose a strong password — at least 8 characters."
      footer={<Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="reset-back-to-login">← Back to sign in</Link>}
    >
      <form onSubmit={onSubmit} data-testid="reset-form" noValidate>
        <Field label="New password">
          <PremiumInput type="password" autoComplete="new-password" size="lg" value={pw} onChange={setPw} placeholder="At least 8 characters" testid="reset-password" autoFocus />
        </Field>
        <Field label="Confirm new password">
          <PremiumInput type="password" autoComplete="new-password" size="lg" value={pw2} onChange={setPw2} placeholder="Re-enter password" testid="reset-password-confirm" />
        </Field>
        {error && <p className="text-[12px] text-rose-600 -mt-1 mb-3" data-testid="reset-error">{error}</p>}
        <button type="submit" disabled={submitting} className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${submitting ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`} data-testid="reset-submit">
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : <><CheckCircle2 size={14} /> Update password</>}
        </button>
      </form>
    </AuthLayout>
  );
}
